import { Alert, Button, DatePicker, Form, Select, TimePicker } from "antd";
import { cajaDelNumero } from "../../utils/numeroDeCajaDeBC";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { getAllCajas } from "../../services/cajaService";
import { getAllTurnos } from "../../services/turnoService";
import { getAllUsers } from "../../services/userService";
import { getCajaHistorico, clearCajaHistorico } from "../../services/cajaEstadoService";
import { showAntdFormError } from "../../utils/common";
import db from "../../db/database";
import DenominacionesCalculator from "../common/DenominacionesCalculator";
import { filtrarCajasPorUsuario } from "../../utils/cajasDelUsuario";
import { getCajaByNoOrId } from "../../services/cajaService";

// Deriva a qué caja pertenece un cierre. Los cierres creados localmente
// (CierreDeCajaForm.js) guardan `idApertura` con el id local (numérico) de
// su apertura relacionada. Los cierres traídos por Traer Histórico en
// cambio quedan con `idApertura: null` (useSyncHistoricos.js los inserta
// así -- el emparejamiento con su apertura lo calcula matchAperturasToCierres
// solo en memoria, para mostrarlo en pantalla, y nunca se persiste). Para
// esos, se deriva la caja del propio `No` compuesto del cierre
// ("TPV;Caja;Fecha;Hora"), mismo formato que usa aperturaDeCaja.No.
const cajaDeCierre = (cierre, aperturasPorId) =>
  aperturasPorId.get(cierre.idApertura)?.Caja || cajaDelNumero(cierre.No);

const AperturaDeCajaForm = ({
  initialData,
  loadingForm = false,
  isCreate = false,
  error = "",
  handleSubmit,
  handleCancel,
  handleError,
  onRefreshDashboard,
}) => {
  const [form] = Form.useForm();
  const [users, setUsers] = useState([]);
  const [turnos, setTurnos] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [cargandoCajas, setCargandoCajas] = useState(true);
  const { currentUser } = useAuth();
  const [importeApertura, setImporteApertura] = useState(0);

  // Detecta conflictos entre la caja que se está por seleccionar y datos
  // locales de OTRA caja, en tres niveles (de más a menos grave):
  //  1) "abierta": alguna apertura de otra caja sigue en Estado "Abierto" --
  //     bloqueo duro, nunca se ofrece borrar (borraría una sesión real que
  //     sigue activa). Hay que cerrar esa caja antes de poder cambiar.
  //  2) "pendiente": hay facturas/aperturas/cierres de otra caja (ya
  //     cerrada) que todavía no se sincronizaron con Business Central --
  //     bloqueo duro, hay que sincronizar primero.
  //  3) "sincronizada": los datos de la otra caja ya están a salvo en BC --
  //     se ofrece borrarlos del dispositivo (no de BC) para poder continuar.
  // Cubre tanto documentos/aperturas creados localmente como los traídos por
  // Traer Histórico (ambos quedan marcados con caja/pendingSync).
  const checkCajaSwitchConflict = async (nuevaCaja) => {
    const abiertaOtraCaja = await db.aperturaDeCaja
      .filter((ap) => !ap.deleted && ap.Estado === "Abierto" && !!ap.Caja && ap.Caja !== nuevaCaja)
      .first();
    if (abiertaOtraCaja) return { tipo: "abierta", caja: abiertaOtraCaja.Caja };

    const invoicesOtraCaja = await db.invoices
      .filter((inv) => !inv.deleted && !!inv.caja && inv.caja !== nuevaCaja)
      .toArray();
    const aperturasOtraCaja = await db.aperturaDeCaja
      .filter((ap) => !ap.deleted && !!ap.Caja && ap.Caja !== nuevaCaja)
      .toArray(); // ninguna en "Abierto": ya se descartó arriba

    // aperturasPorId necesita TODAS las aperturas (no solo las de otra caja)
    // para que cajaDeCierre pueda resolver el idApertura de cualquier cierre.
    const aperturasPorId = new Map((await db.aperturaDeCaja.toArray()).map((ap) => [ap.id, ap]));
    const cierresOtraCaja = (await db.cierreDeCaja.filter((c) => !c.deleted).toArray()).filter(
      (c) => {
        const caja = cajaDeCierre(c, aperturasPorId);
        return !!caja && caja !== nuevaCaja;
      }
    );

    if (!invoicesOtraCaja.length && !aperturasOtraCaja.length && !cierresOtraCaja.length) {
      return null;
    }

    const cajaConflicto =
      invoicesOtraCaja[0]?.caja ||
      aperturasOtraCaja[0]?.Caja ||
      cajaDeCierre(cierresOtraCaja[0], aperturasPorId);
    const invoicesDeEsaCaja = invoicesOtraCaja.filter((inv) => inv.caja === cajaConflicto);
    const aperturasDeEsaCaja = aperturasOtraCaja.filter((ap) => ap.Caja === cajaConflicto);
    const cierresDeEsaCaja = cierresOtraCaja.filter(
      (c) => cajaDeCierre(c, aperturasPorId) === cajaConflicto
    );

    const pendiente =
      invoicesDeEsaCaja.some((d) => d.pendingSync === 1) ||
      aperturasDeEsaCaja.some((d) => d.pendingSync === 1) ||
      cierresDeEsaCaja.some((d) => d.pendingSync === 1);

    return {
      tipo: pendiente ? "pendiente" : "sincronizada",
      caja: cajaConflicto,
      counts: {
        invoices: invoicesDeEsaCaja.length,
        aperturas: aperturasDeEsaCaja.length,
        cierres: cierresDeEsaCaja.length,
      },
    };
  };

  const handleCajaChange = async (value) => {
    if (!isCreate) {
      form.setFieldValue("Caja", value);
      return;
    }

    const valorAnterior = form.getFieldValue("Caja");
    const conflict = await checkCajaSwitchConflict(value);

    if (!conflict) {
      form.setFieldValue("Caja", value);
      return;
    }

    // revertir visualmente hasta que se resuelva el conflicto
    form.setFieldValue("Caja", valorAnterior);

    if (conflict.tipo === "abierta") {
      window.messageApi.error(
        <p>
          No se puede abrir una caja nueva mientras la caja {conflict.caja} siga abierta. Ciérrela
          antes de continuar.
        </p>,
        {
          modal: true,
          title: "Cambio de caja no permitido",
          okText: "Entendido",
          cancelButtonProps: { style: { display: "none" } },
        }
      );
      return;
    }

    if (conflict.tipo === "pendiente") {
      window.messageApi.error(
        <>
          <p>
            No es posible abrir otra caja: hay documentos, aperturas o cierres de la caja{" "}
            {conflict.caja} que todavía no se han sincronizado con Business Central.
          </p>
          <p>Sincronice esos datos antes de intentar abrir una caja distinta.</p>
        </>,
        {
          modal: true,
          title: "Cambio de caja no permitido",
          okText: "Entendido",
          cancelButtonProps: { style: { display: "none" } },
        }
      );
      return;
    }

    const { invoices, aperturas, cierres } = conflict.counts;
    const partes = [
      invoices > 0 && `${invoices} documento${invoices === 1 ? "" : "s"}`,
      aperturas > 0 && `${aperturas} apertura${aperturas === 1 ? "" : "s"}`,
      cierres > 0 && `${cierres} cierre${cierres === 1 ? "" : "s"}`,
    ].filter(Boolean);

    window.messageApi.confirm({
      title: "Cambio de caja",
      content: (
        <>
          <p>
            Hay {partes.join(", ")} de la caja {conflict.caja} guardados localmente, ya
            sincronizados con Business Central.
          </p>
          <p>
            Si continúa, esos datos se eliminarán de este dispositivo para poder abrir la caja{" "}
            {value}. ¿Desea continuar?
          </p>
        </>
      ),
      okText: "Sí, eliminar y continuar",
      cancelText: "No",
      onOk: async () => {
        const invoicesToDelete = await db.invoices
          .filter((inv) => inv.caja === conflict.caja)
          .toArray();
        const invoiceIds = new Set(invoicesToDelete.map((inv) => inv.id));
        await db.invoiceLines.filter((l) => invoiceIds.has(l.invoiceId)).delete();
        await db.invoicePaymentLines.filter((l) => invoiceIds.has(l.invoiceId)).delete();
        await db.invoices.filter((inv) => inv.caja === conflict.caja).delete();

        const aperturasPorId = new Map(
          (await db.aperturaDeCaja.toArray()).map((ap) => [ap.id, ap])
        );
        const aperturasToDelete = await db.aperturaDeCaja
          .filter((ap) => ap.Caja === conflict.caja)
          .toArray();
        const aperturaIds = new Set(aperturasToDelete.map((ap) => ap.id));
        const cierresToDelete = (await db.cierreDeCaja.toArray()).filter(
          (c) => cajaDeCierre(c, aperturasPorId) === conflict.caja
        );
        const cierreIds = new Set(cierresToDelete.map((c) => c.id));
        await db.cierreDeCajaDetalle.filter((d) => cierreIds.has(d.idCierre)).delete();
        await db.cierreDeCaja.filter((c) => cierreIds.has(c.id)).delete();
        await db.aperturaDeCaja.filter((ap) => aperturaIds.has(ap.id)).delete();

        clearCajaHistorico();

        form.setFieldValue("Caja", value);

        if (onRefreshDashboard) {
          onRefreshDashboard();
        }
      },
      onCancel: () => {
        form.setFieldValue("Caja", valorAnterior);
      },
    });
  };

  // Cargar cajeros (usuarios), turnos y cajas
  useEffect(() => {
    const loadData = async () => {
      try {
        const usersData = await getAllUsers();
        setUsers(usersData);

        const turnosData = await getAllTurnos();
        setTurnos(turnosData);

        const cajasData = await getAllCajas();
        setCajas(filtrarCajasPorUsuario(cajasData, currentUser));
        setCargandoCajas(false);

        const cajaHistoricoData = getCajaHistorico();

        if (cajaHistoricoData?.Caja) {
          form.setFieldValue("Caja", cajaHistoricoData.Caja);
        }

        // Establecer el primer turno por defecto si no estamos editando
        if (!initialData && turnosData.length > 0) {
          form.setFieldValue("Turno", turnosData[0].No || turnosData[0].id);
        }
      } catch (error) {
        console.error("Error al cargar datos:", error);
        setCargandoCajas(false);
        handleError("Error al cargar datos de cajeros, turnos y cajas");
      }
    };

    loadData();
    // `handleError` es el setError del componente padre: viene de useState, así
    // que su identidad no cambia y declararlo no altera cuándo corre el efecto.
  }, [form, initialData, currentUser, handleError]);

  // Cargar datos de la apertura si estamos en modo edición
  useEffect(() => {
    if (initialData) {
      setImporteApertura(initialData.ImporteDeApertura || 0);
      form.setFieldsValue({
        Cajero: initialData.Cajero,
        FechaDeApertura: initialData.FechaDeApertura ? dayjs(initialData.FechaDeApertura) : null,
        HoraDeApertura: initialData.HoraDeApertura
          ? dayjs(initialData.HoraDeApertura, "HH:mm")
          : null,
        Estado: initialData.Estado,
        Turno: initialData.Turno,
        Caja: initialData.Caja,
      });
    } else {
      setImporteApertura(0);
      form.resetFields();
      // Establecer valores predeterminados
      const horaActual = dayjs();
      form.setFieldsValue({
        FechaDeApertura: dayjs(),
        HoraDeApertura: horaActual,
        Estado: "Abierto", // Estado por defecto
        Cajero: currentUser?.No, // Establecer el usuario actual como cajero predeterminado
      });
    }
  }, [initialData, form, currentUser]);

  const onFinish = async (values) => {
    // La tienda de la apertura, con red por debajo.
    //
    // Se leía sólo de la lista que tiene el desplegable en memoria, y si ese
    // registro venía sin tienda la apertura se guardaba muda: sin `TPV` no hay
    // almacén, sin almacén no hay stock que traer, y la sincronización de
    // productos contestaba «No hay caja abierta» delante de una caja abierta.
    // Además la apertura viaja a Business Central con su tienda, así que
    // guardarla sin ella no es sólo un problema de esta pantalla.
    const cajaEnLaLista = cajas.find((item) => item.No === values.Caja);
    let tienda = String(cajaEnLaLista?.TPV ?? "").trim();
    if (!tienda && values.Caja) {
      try {
        const enElMaestro = await getCajaByNoOrId(values.Caja);
        tienda = String(enElMaestro?.TPV ?? "").trim();
      } catch {
        tienda = "";
      }
    }
    if (!tienda) {
      window.messageApi.warning(
        `La caja ${values.Caja} no tiene una tienda asignada en este equipo. La apertura se guarda, pero no se podrán traer los productos hasta sincronizar el maestro de Cajas.`
      );
    }

    const formattedValues = {
      ...values,
      TPV: tienda || undefined,
      ImporteDeApertura: importeApertura,
      FechaDeApertura: values.FechaDeApertura.format("YYYY-MM-DD"),
      HoraDeApertura: values.HoraDeApertura.format("HH:mm"),
    };

    handleSubmit(formattedValues);
  };

  const estadoOptions = [
    { value: "Abierto", label: "Abierto" },
    { value: "Cerrado", label: "Cerrado" },
    { value: "Turno Generado", label: "Turno Generado" },
  ];

  const handleImporteChange = (nuevoImporte) => {
    setImporteApertura(nuevoImporte);
  };

  return (
    <div>
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          onClose={() => handleError("")}
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        form={form}
        name="apertura-caja-form"
        onFinish={onFinish}
        onFinishFailed={(err) => showAntdFormError(err)}
        layout="vertical"
        scrollToFirstError
      >
        <Form.Item name="Cajero" label="Cajero">
          <Select
            placeholder="Seleccione un cajero"
            loading={users.length === 0}
            showSearch
            optionFilterProp="children"
            disabled
            filterOption={(input, option) =>
              (option?.label || "").toLowerCase().includes(input.toLowerCase())
            }
          >
            {users.map((user) => (
              <Select.Option key={user.id} value={user.No}>
                {user.firstName} {user.lastName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="Caja"
          label="Caja"
          rules={[
            {
              required: true,
              message: "Por favor seleccione la caja",
            },
          ]}
        >
          <Select
            placeholder="Seleccione una caja"
            loading={cargandoCajas}
            notFoundContent={
              cargandoCajas ? null : "No hay cajas de su tienda sincronizadas en este equipo"
            }
            showSearch
            optionFilterProp="children"
            onChange={handleCajaChange}
            filterOption={(input, option) =>
              (option?.label || "").toLowerCase().includes(input.toLowerCase())
            }
          >
            {cajas.map((caja) => (
              <Select.Option key={caja.id} value={caja.No}>
                {caja.nombre}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="FechaDeApertura" label="Fecha de apertura">
          <DatePicker disabled style={{ width: "100%" }} format="DD/MM/YYYY" />
        </Form.Item>

        <Form.Item name="HoraDeApertura" label="Hora de apertura">
          <TimePicker disabled style={{ width: "100%" }} format="HH:mm" minuteStep={5} />
        </Form.Item>

        <Form.Item
          label="Importe de apertura"
          required
          tooltip="Utilice el botón de calculadora para ingresar el importe por denominaciones"
        >
          <DenominacionesCalculator
            value={importeApertura}
            onChange={handleImporteChange}
            apertura={true}
            modalTitle="Cálculo de Importe por Denominaciones"
            placeholder="Importe de apertura"
          />
        </Form.Item>

        <Form.Item name="Estado" label="Estado">
          <Select placeholder="Seleccione el estado" disabled>
            {estadoOptions.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="Turno"
          label="Turno"
          rules={[
            {
              required: true,
              message: "Por favor seleccione el turno",
            },
          ]}
        >
          <Select
            placeholder="Seleccione un turno"
            loading={turnos.length === 0}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label || "").toLowerCase().includes(input.toLowerCase())
            }
          >
            {turnos.map((turno) => (
              <Select.Option key={turno.No || turno.id} value={turno.No || turno.id}>
                {turno.DescripcionTurno}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item>
          <div style={{ display: "flex", gap: "10px" }}>
            <Button type="primary" htmlType="submit" loading={loadingForm} block>
              {isCreate ? "Registrar Apertura de Caja" : "Actualizar Apertura de Caja"}
            </Button>

            <Button onClick={() => handleCancel(form)} block>
              Cancelar
            </Button>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
};

export default AperturaDeCajaForm;
